<template>
  <div
    class="base-combobox"
    :class="[
      `base-combobox--${size}`,
      {
        'is-open': isOpen,
        'is-disabled': disabled,
        'base-combobox--icon': variant === 'icon',
        'base-combobox--list': isList,
      },
    ]"
    @keydown="onKeydown"
  >
    <!-- Icon trigger (variant 'icon'): a compact filter-chip-style button — the
         icon, a selection count, and the caret. Same dropdown below. -->
    <button
      v-if="variant === 'icon'"
      ref="control"
      type="button"
      class="base-combobox__icon-trigger"
      :class="{ 'is-on': hasSelection }"
      role="combobox"
      :aria-expanded="String(isOpen)"
      aria-haspopup="true"
      :aria-label="placeholder"
      :disabled="disabled"
      v-tooltip="{ content: placeholder || '', placement: 'top', delay: 400 }"
      @click="toggleOpen"
    >
      <BaseIcon :name="icon" class="base-combobox__icon-trigger-ico" />
      <span v-if="chips.length" class="base-combobox__icon-trigger-badge">({{ chips.length }})</span>
      <BaseIcon name="chevron-down" class="base-combobox__caret" :class="{ 'is-open': isOpen }" />
    </button>

    <!-- Control face (variant 'field'): selected parent chips + placeholder + clear/caret. -->
    <div
      v-else-if="!isList"
      ref="control"
      class="base-combobox__control"
      role="combobox"
      :aria-expanded="String(isOpen)"
      aria-haspopup="true"
      :tabindex="disabled ? -1 : 0"
      @click="toggleOpen"
    >
      <div ref="tags" class="base-combobox__tags">
        <span v-if="!chips.length" class="base-combobox__placeholder">{{ placeholder }}</span>

        <BaseTag v-for="chip in visibleChips" :key="chip.key" :tone="tone" preset="soft" class="base-combobox__chip">
          <span class="base-combobox__chip-inner">
            <span class="base-combobox__chip-label">{{ chip.label }}</span>
            <span v-if="chip.count" class="base-combobox__chip-count">{{ chip.count }}</span>
            <button
              type="button"
              class="base-combobox__chip-remove"
              :aria-label="removeLabel"
              @click.stop="removeChip(chip)"
            >
              <BaseIcon name="x" />
            </button>
          </span>
        </BaseTag>

        <span v-if="hiddenChipCount" class="base-combobox__more">+{{ hiddenChipCount }}</span>
      </div>

      <div class="base-combobox__actions">
        <button
          v-if="hasSelection"
          type="button"
          class="base-combobox__clear"
          :aria-label="clearLabel"
          @click.stop="clearAll"
        >
          <BaseIcon name="x-circle" />
        </button>
        <BaseIcon name="chevron-down" class="base-combobox__caret" :class="{ 'is-open': isOpen }" />
      </div>
    </div>

    <!-- Dropdown: optional select-all bar, then the option tree (any depth). -->
    <transition name="cbx-menu">
      <div v-if="isOpen" v-closable="onClickOutside" class="base-combobox__menu">
        <div v-if="searchable" class="base-combobox__search">
          <BaseIcon name="search" class="base-combobox__search-icon" />
          <input
            ref="search"
            v-model="query"
            type="text"
            class="base-combobox__search-input"
            :placeholder="searchPlaceholder"
          />
        </div>

        <div
          v-if="selectAllLabel && !isSearching"
          :id="itemId('all')"
          class="base-combobox__selectall"
          :class="{ 'is-active': allSelected || someSelected, 'is-highlighted': activeId === itemId('all') }"
          @mouseenter="activeId = itemId('all')"
        >
          <FormCheckbox
            size="small"
            :value="allSelected"
            :indeterminate="someSelected"
            :label="`${selectAllLabel} (${totalLeaves})`"
            @input="toggleSelectAll"
          />
        </div>

        <div class="base-combobox__options">
          <p v-if="!visibleOptions.length" class="base-combobox__empty">{{ emptyText }}</p>

          <!-- Each top-level option renders as a node; nodes render their own
               children recursively, so the tree can be any depth. -->
          <BaseComboBoxNode v-for="group in visibleOptions" :key="group.value" :node="group" :depth="0" />
        </div>

        <!-- Clear-all footer (opt-in via `clearable`), mirroring FilterChip's
             popover clear: full-width, divider above, deselects everything. -->
        <BaseButton
          v-if="clearable && hasSelection"
          class="base-combobox__menu-clear"
          isSmall
          isTransparent
          isSquare
          @click.native="clearAll"
        >
          {{ clearLabel }}
        </BaseButton>
      </div>
    </transition>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseComboBoxNode from '@/components/base/BaseComboBoxNode';
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag';
import FormCheckbox from '@/components/form/FormCheckbox';
import { isTone, isSize } from '@/util/design-tokens';

// Space (px) reserved on the single-line control for the "+N" overflow pill when
// the selected chips don't all fit. Only affects how many chips stay visible.
const OVERFLOW_COUNTER_WIDTH = 44;

// Grouped multi-select over an option tree of any depth (e.g. province ->
// région -> ville). Only leaves carry values: a group's checkbox selects every
// leaf under it (tri-state), the control chips are the top-level groups with
// their selected-leaf count, and select-all covers every leaf in the tree.
// Node values must be unique across the whole tree (they key rows and expansion).
export default {
  name: 'BaseComboBox',
  components: { BaseButton, BaseComboBoxNode, BaseIcon, BaseTag, FormCheckbox },
  // The recursive node rows read selection/expansion state and call back into
  // this instance (see BaseComboBoxNode).
  provide() {
    return { combobox: this };
  },
  props: {
    // v-model: flat array of selected leaf values (the values of childless
    // nodes at any depth). Group/select-all state is derived.
    value: {
      type: Array,
      default: () => [],
    },
    // Option tree, any depth: [{ value, label, children?: [node] }].
    // A node without children is a directly selectable leaf.
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    // When null, the select-all row is hidden. Pass the translated base label
    // (e.g. $t(...)); the "(N)" count is appended by the component.
    selectAllLabel: {
      type: String,
      default: null,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: '',
    },
    emptyText: {
      type: String,
      default: '',
    },
    removeLabel: {
      type: String,
      default: '',
    },
    clearLabel: {
      type: String,
      default: '',
    },
    // Adds a clear-all button at the bottom of the dropdown (labelled with
    // `clearLabel`) that deselects every value. Off by default.
    clearable: {
      type: Boolean,
      default: false,
    },
    // 'field' renders the classic chips-in-a-textbox control; 'icon' renders a
    // compact icon-only trigger (filter-chip style) with a selection count; 'list'
    // renders no trigger, only the search and the tree, open in place (a phone's
    // filter sheet holds it).
    variant: {
      type: String,
      default: 'field',
      validator: (value) => ['field', 'icon', 'list'].includes(value),
    },
    // Icon shown by the 'icon' variant trigger.
    icon: {
      type: String,
      default: 'map-pin',
    },
    size: {
      type: String,
      default: 'md',
      validator: isSize,
    },
    tone: {
      type: String,
      default: 'info',
      validator: isTone,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // The list variant is open from its first frame.
      isOpen: this.variant === 'list',
      query: '',
      expandedKeys: [],
      // id of the keyboard-highlighted row (roving highlight); null = none.
      activeId: null,
      // Max chips to render on the single-line control; null = all (also the
      // pre-measure state). Set by measureChips().
      maxVisibleChips: null,
    };
  },
  computed: {
    isList() {
      return this.variant === 'list';
    },
    // Safely normalise `value` to an Array to prevent runtime crashes if passed null/undefined.
    selectedValueArray() {
      return Array.isArray(this.value) ? this.value : [];
    },
    // Per-instance id prefix, shared by every navigable row.
    idPrefix() {
      return `cbx${this._uid}`;
    },
    // Selected values as a Set for O(1) membership tests in the render path
    // (rebuilt only when `value` changes).
    selectedSet() {
      return new Set(this.selectedValueArray);
    },
    // "Search mode" as one derived flag, so every search-sensitive behaviour
    // (force-expand, hide chevron, hide select-all) agrees; trims so a
    // whitespace-only query is not treated as a search.
    isSearching() {
      return this.searchable && this.query.trim().length > 0;
    },
    // Every selectable leaf value across the tree, at any depth. Drives
    // select-all state and the count badge.
    leafValues() {
      return (this.options || []).flatMap((group) => this.getGroupChildValues(group));
    },
    totalLeaves() {
      return this.leafValues.length;
    },
    hasSelection() {
      return this.selectedValueArray.length > 0;
    },
    allSelected() {
      return this.totalLeaves > 0 && this.leafValues.every((v) => this.selectedSet.has(v));
    },
    someSelected() {
      return !this.allSelected && this.leafValues.some((v) => this.selectedSet.has(v));
    },
    // Top-level chip + count model. A grouped chip shows the number of selected
    // leaves under it (at any depth); a childless leaf shows no count.
    chips() {
      const set = this.selectedSet;
      return (this.options || []).reduce((acc, group) => {
        if (this.hasChildren(group)) {
          const count = this.getGroupChildValues(group).filter((v) => set.has(v)).length;
          if (count > 0) acc.push({ key: group.value, label: group.label, count });
        } else if (set.has(group.value)) {
          acc.push({ key: group.value, label: group.label, count: 0 });
        }
        return acc;
      }, []);
    },
    // Chips shown on the single-line control (measureChips caps this); the rest
    // roll up into the "+N" pill.
    visibleChips() {
      return this.maxVisibleChips == null ? this.chips : this.chips.slice(0, this.maxVisibleChips);
    },
    hiddenChipCount() {
      return this.maxVisibleChips == null ? 0 : Math.max(0, this.chips.length - this.maxVisibleChips);
    },
    // Search keeps a node whole when its own label matches, else keeps it with
    // only its matching descendants (recursively); nodes with neither drop out.
    // Only active when `searchable` and a query set.
    visibleOptions() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.options || [];
      const matches = (node) =>
        String(node.label || '')
          .toLowerCase()
          .includes(q);
      const filterNode = (node) => {
        if (matches(node)) return node;
        if (!this.hasChildren(node)) return null;
        const children = node.children.map(filterNode).filter(Boolean);
        return children.length ? { ...node, children } : null;
      };
      return (this.options || []).map(filterNode).filter(Boolean);
    },
    // Flat, ordered list of keyboard-navigable rows, matching render order:
    // select-all, then every visible node depth-first, descending only into
    // expanded groups.
    navItems() {
      const items = [];
      if (this.selectAllLabel && !this.isSearching) items.push({ id: this.itemId('all'), type: 'all' });
      const walk = (node) => {
        if (this.hasChildren(node)) {
          items.push({ id: this.itemId('group', node), type: 'group', group: node });
          if (this.isExpanded(node)) node.children.forEach(walk);
        } else {
          items.push({ id: this.itemId('child', node), type: 'child', child: node });
        }
      };
      this.visibleOptions.forEach(walk);
      return items;
    },
  },
  watch: {
    // A new search resets the highlight to the first match so Enter selects it.
    query() {
      if (this.isOpen) this.activeId = this.firstNavId();
    },
    // The visible chip set changed, so recompute how many fit on one line.
    chips() {
      this.measureChips();
    },
  },
  methods: {
    hasChildren(node) {
      return Array.isArray(node.children) && node.children.length > 0;
    },
    // Every leaf value under a node, at any depth (the node itself when a leaf).
    getGroupChildValues(node) {
      return this.hasChildren(node) ? node.children.flatMap((child) => this.getGroupChildValues(child)) : [node.value];
    },
    isChildSelected(child) {
      return this.selectedSet.has(child.value);
    },
    // Resolved checkbox state for any row: a leaf is on or off; a group is
    // selected when every leaf under it is, indeterminate when only some are.
    groupState(node) {
      const set = this.selectedSet;
      if (!this.hasChildren(node)) {
        return { selected: set.has(node.value), indeterminate: false };
      }
      const leaves = this.getGroupChildValues(node);
      const selected = leaves.filter((v) => set.has(v)).length;
      return {
        selected: selected === leaves.length,
        indeterminate: selected > 0 && selected < leaves.length,
      };
    },
    // The roving keyboard highlight (also set by mouse hover on a row).
    setActive(id) {
      this.activeId = id;
    },
    isExpanded(group) {
      // While searching, every matching group is forced open so the matching
      // villes show under their région (e.g. "Bro" reveals Brossard).
      if (this.isSearching) return true;
      return this.expandedKeys.includes(group.value);
    },
    toggleExpand(group) {
      this.expandedKeys = this.isExpanded(group)
        ? this.expandedKeys.filter((k) => k !== group.value)
        : [...this.expandedKeys, group.value];
    },
    toggleChild(child) {
      const arr = this.selectedValueArray;
      this.emitValue(arr.includes(child.value) ? arr.filter((v) => v !== child.value) : [...arr, child.value]);
    },
    toggleGroup(group) {
      if (!this.hasChildren(group)) {
        this.toggleChild({ value: group.value });
        return;
      }
      const childValues = this.getGroupChildValues(group);
      const arr = this.selectedValueArray;
      const allOn = childValues.every((v) => arr.includes(v));
      this.emitValue(allOn ? arr.filter((v) => !childValues.includes(v)) : [...new Set([...arr, ...childValues])]);
    },
    toggleSelectAll() {
      this.emitValue(this.allSelected ? [] : [...this.leafValues]);
    },
    removeChip(chip) {
      const group = (this.options || []).find((g) => g.value === chip.key);
      const arr = this.selectedValueArray;
      if (group && this.hasChildren(group)) {
        const childValues = this.getGroupChildValues(group);
        this.emitValue(arr.filter((v) => !childValues.includes(v)));
      } else {
        this.emitValue(arr.filter((v) => v !== chip.key));
      }
    },
    clearAll() {
      this.emitValue([]);
    },
    emitValue(next) {
      this.$emit('input', next);
      this.$emit('change', next);
    },
    toggleOpen() {
      if (this.disabled) return;
      this.isOpen ? this.closeMenu() : this.openMenu();
    },
    openMenu({ focus = true } = {}) {
      this.isOpen = true;
      // Auto-expand groups (at any depth) that already have a selection so the
      // user sees their current choices without hunting through collapsed parents.
      const set = this.selectedSet;
      const withSelection = [];
      const collect = (node) => {
        if (!this.hasChildren(node)) return;
        if (this.getGroupChildValues(node).some((v) => set.has(v))) withSelection.push(node.value);
        node.children.forEach(collect);
      };
      (this.options || []).forEach(collect);
      this.expandedKeys = [...new Set([...this.expandedKeys, ...withSelection])];
      if (this.searchable && focus) {
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
      }
    },
    closeMenu() {
      // The list variant has no menu to close: it is the list.
      if (this.isList) return;
      this.isOpen = false;
      this.query = '';
      this.activeId = null;
      window.clearTimeout(this._taTimer);
      this._taBuffer = '';
    },
    // v-closable resolves this method by name (see directives/closable.js).
    onClickOutside(e) {
      if (e && this.$refs.control && this.$refs.control.contains(e.target)) {
        return;
      }
      this.closeMenu();
    },
    // Stable per-instance row ids for the roving highlight and scroll-into-view.
    // Assumes node values are unique across the whole tree.
    itemId(type, ref) {
      if (type === 'all') return `${this.idPrefix}-all`;
      if (type === 'group') return `${this.idPrefix}-g-${ref.value}`;
      return `${this.idPrefix}-c-${ref.value}`;
    },
    firstNavId() {
      return this.navItems.length ? this.navItems[0].id : null;
    },
    currentItem() {
      return this.navItems.find((it) => it.id === this.activeId) || null;
    },
    // Central keyboard handler for the whole widget. Only hijacks keys while the
    // control or the search input holds focus, so Tabbed-to inner checkboxes and
    // buttons keep their native behaviour.
    onKeydown(e) {
      if (this.disabled) return;
      const key = e.key;

      if (!this.isOpen) {
        if ((key === 'Enter' || key === ' ' || key === 'ArrowDown') && e.target === this.$refs.control) {
          e.preventDefault();
          this.openMenu();
          this.$nextTick(() => {
            this.activeId = this.firstNavId();
          });
        }
        return;
      }

      if (key === 'Escape' || key === 'Esc') {
        e.preventDefault();
        this.closeMenu();
        this.focusControl();
        return;
      }

      const inSearch = this.searchable && e.target === this.$refs.search;
      const isHolder = e.target === this.$refs.control || inSearch;
      if (!isHolder) return;

      switch (key) {
        case 'ArrowDown':
          e.preventDefault();
          this.moveActive(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.moveActive(-1);
          break;
        case 'Home':
        case 'End':
          if (!inSearch) {
            e.preventDefault();
            this.moveEdge(key === 'Home');
          }
          break;
        case 'Enter':
        case ' ':
          // Space types in the search box; Enter and out-of-search Space toggle.
          if (key === 'Enter' || !inSearch) {
            e.preventDefault();
            this.activateCurrent();
          }
          break;
        case 'ArrowRight':
        case 'ArrowLeft':
          if (!inSearch) {
            e.preventDefault();
            this.setExpanded(key === 'ArrowRight');
          }
          break;
        case 'Tab':
          this.closeMenu();
          break;
        default:
          // Type-ahead only when there's no search box to type into.
          if (!this.searchable && key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            this.typeahead(key);
          }
      }
    },
    moveActive(delta) {
      const items = this.navItems;
      if (!items.length) return;
      let i = items.findIndex((it) => it.id === this.activeId);
      if (i === -1) i = delta > 0 ? 0 : items.length - 1;
      else i = Math.min(items.length - 1, Math.max(0, i + delta));
      this.activeId = items[i].id;
      this.scrollActiveIntoView();
    },
    moveEdge(first) {
      const items = this.navItems;
      if (!items.length) return;
      this.activeId = (first ? items[0] : items[items.length - 1]).id;
      this.scrollActiveIntoView();
    },
    activateCurrent() {
      const it = this.currentItem();
      if (!it) return;
      if (it.type === 'all') this.toggleSelectAll();
      else if (it.type === 'group') this.toggleGroup(it.group);
      else this.toggleChild(it.child);
    },
    // ArrowRight/Left on a group expands/collapses it (non-search mode only).
    setExpanded(expand) {
      const it = this.currentItem();
      if (!it || it.type !== 'group' || !this.hasChildren(it.group)) return;
      const isExp = this.isExpanded(it.group);
      if (expand !== isExp) this.toggleExpand(it.group);
    },
    scrollActiveIntoView() {
      this.$nextTick(() => {
        if (!this.activeId) return;
        // ids carry this._uid, so they are document-unique.
        const el = document.getElementById(this.activeId);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    },
    // Non-searchable type-ahead: buffer keystrokes briefly and jump to the first
    // row whose label starts with the buffer.
    typeahead(char) {
      window.clearTimeout(this._taTimer);
      this._taBuffer = (this._taBuffer || '') + char.toLowerCase();
      const buffer = this._taBuffer;
      const match = this.navItems.find((it) => {
        const rawLabel = it.type === 'all' ? '' : it.child ? it.child.label : it.group ? it.group.label : '';
        return String(rawLabel || '')
          .toLowerCase()
          .startsWith(buffer);
      });
      if (match) {
        this.activeId = match.id;
        this.scrollActiveIntoView();
      }
      this._taTimer = window.setTimeout(() => {
        this._taBuffer = '';
      }, 600);
    },
    focusControl() {
      this.$nextTick(() => {
        if (this.$refs.control) this.$refs.control.focus();
      });
    },
    // Keep the control to one line: render all chips, measure their widths, then
    // cap `maxVisibleChips` to those that fit (reserving room for the "+N" pill).
    measureChips() {
      // Show all first so every chip has a real width to measure.
      this.maxVisibleChips = null;
      this.$nextTick(() => {
        const container = this.$refs.tags;
        if (!container) return;
        const chipEls = Array.from(container.querySelectorAll('.base-combobox__chip'));
        if (!chipEls.length) return;
        const styles = window.getComputedStyle(container);
        const gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
        const available = container.clientWidth;
        const widths = chipEls.map((el) => el.offsetWidth);
        const totalAll = widths.reduce((sum, w, i) => sum + w + (i ? gap : 0), 0);
        if (totalAll <= available) {
          this.maxVisibleChips = null; // everything fits, no counter needed
          return;
        }
        const reserve = OVERFLOW_COUNTER_WIDTH + gap;
        let used = 0;
        let fit = 0;
        for (let i = 0; i < widths.length; i++) {
          const w = widths[i] + (i ? gap : 0);
          if (used + w + reserve > available) break;
          used += w;
          fit += 1;
        }
        this.maxVisibleChips = fit;
      });
    },
  },
  mounted() {
    // Without focusing the search: on a phone that would bring up the keyboard over the list.
    if (this.isList) this.openMenu({ focus: false });
    this.measureChips();
    // Re-measure when the control's width changes (window resize, clear button
    // appearing/disappearing). Content-only changes go through the chips watcher.
    if (typeof ResizeObserver !== 'undefined' && this.$refs.tags) {
      this._resizeObserver = new ResizeObserver(() => this.measureChips());
      this._resizeObserver.observe(this.$refs.tags);
    }
  },
  beforeDestroy() {
    window.clearTimeout(this._taTimer);
    if (this._resizeObserver) this._resizeObserver.disconnect();
  },
};
</script>

<style lang="scss" scoped>
// Shorthand for the nested $aloha-sizes lookup used throughout this file.
@function aloha-size($size, $key) {
  @return map-get(map-get($aloha-sizes, $size), $key);
}

.base-combobox {
  position: relative;
  width: 100%;
  font-family: $font-family-body;

  // List variant: the dropdown's content in the flow, as tall as its options (what holds it scrolls).
  &--list .base-combobox__menu {
    position: static;
    max-height: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  // Icon variant: the root shrinks to its trigger; the dropdown gets its own width.
  &--icon {
    width: auto;
    display: inline-flex;

    .base-combobox__menu {
      width: 320px;
      max-width: 80vw;
    }
  }

  // Compact icon-only trigger, mirroring the filter-chip button face.
  &__icon-trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: $color-white;
    border: 1px solid transparent;
    box-shadow: $shadow-sm;
    border-radius: $radius-md;
    padding: 7px 11px;
    min-height: 32px;
    font-family: inherit;
    font-size: 13px;
    font-weight: bold;
    color: $color-text-body;
    cursor: pointer;
    transition: background ease 100ms;

    &:hover {
      background: $color-hover;
    }

    &.is-on {
      background: $color-accent-100;
      outline: 1.5px solid $color-accent-300;
      color: $color-accent;

      &:hover {
        background: $color-accent-200;
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__icon-trigger-ico {
    width: 15px;
    height: 15px;
    flex: none;
  }

  &__icon-trigger-badge {
    font-variant-numeric: tabular-nums;
  }

  &__control {
    display: flex;
    align-items: center;
    gap: aloha-size('sm', 'gap');
    box-sizing: border-box;
    width: 100%;
    background-color: $color-white;
    border: 1px solid $color-input-border;
    border-radius: $border-radius-root;
    cursor: pointer;
    transition: border-color $duration-fast $ease-ui-interaction, box-shadow $duration-fast $ease-ui-interaction;

    &:hover {
      border-color: $color-slate-400;
    }

    &:focus-visible {
      outline: none;
    }
  }

  // Same accent ring for keyboard focus and the open state.
  &.is-open &__control,
  &__control:focus-visible {
    border-color: $color-accent-500;
    box-shadow: $shadow-focus;
  }

  &.is-disabled &__control {
    background-color: $color-surface-sunken;
    cursor: not-allowed;
    pointer-events: none;
  }

  // Single line: chips that don't fit are replaced by the "+N" pill
  // (see measureChips), so the control never grows past one row.
  &__tags {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: nowrap;
    align-items: center;
    gap: aloha-size('xs', 'gap');
    min-width: 0;
    overflow: hidden;
  }

  &__placeholder {
    color: $color-text-placeholder;
  }

  &__chip {
    flex: 0 0 auto;
    max-width: 100%;
  }

  // Overflow pill: "+N" régions that don't fit on the single line.
  &__more {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    min-height: aloha-size('xs', 'height');
    padding: 0 aloha-size('xs', 'px-round');
    border-radius: $radius-full;
    background-color: $color-slate-100;
    color: $color-text-body;
    font-size: aloha-size('sm', 'font-size');
    font-weight: $font-weight-bold;
    white-space: nowrap;
  }

  // Row wrapper: keeps label, count, and remove vertically centred inside the
  // BaseTag slot (which is otherwise an inline baseline context).
  &__chip-inner {
    display: inline-flex;
    align-items: center;
    gap: aloha-size('sm', 'gap');
    min-width: 0;
  }

  &__chip-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chip-count {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 aloha-size('xxs', 'px');
    border-radius: $radius-full;
    background-color: $color-white;
    color: $color-text-body;
    font-size: aloha-size('xs', 'font-size');
    font-weight: $font-weight-bold;
    line-height: 1;
  }

  // Remove control sits at the tag's own scale, not the control's icon scale.
  &__chip-remove {
    flex: 0 0 auto;
    width: aloha-size('md', 'icon-size');
    height: aloha-size('md', 'icon-size');

    svg {
      width: 100%;
      height: 100%;
    }
  }

  &__chip-remove,
  &__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: currentColor;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity $duration-fast $ease-ui-interaction, transform $duration-fast $ease-ui-interaction;

    &:hover {
      opacity: 1;
    }

    // Press feedback: the control confirms it heard the tap.
    &:active {
      transform: scale(0.9);
    }
  }

  &__actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: aloha-size('xs', 'gap');
    margin-left: auto;
  }

  &__clear {
    color: $color-text-muted;
  }

  &__caret {
    color: $color-text-muted;
    transition: transform $duration-fast $ease-ui-interaction;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__menu {
    position: absolute;
    top: calc(100% + #{$space-1});
    left: 0;
    z-index: $z-index-dropdown;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    max-height: 320px;
    overflow: hidden;
    background-color: $color-white;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    box-shadow: $shadow-dropdown;
    // Anchored below the control, so it scales in from its top edge.
    transform-origin: top;
  }

  // Dropdown entrance: opacity + scale from the trigger edge. Exit is snappier
  // than enter; reduced-motion (below) keeps the fade but drops the movement.
  .cbx-menu-enter-active,
  .cbx-menu-leave-active {
    transition: opacity $duration-fast $ease-ui-interaction, transform $duration-fast $ease-ui-interaction;
  }

  .cbx-menu-enter,
  .cbx-menu-leave-to {
    opacity: 0;
    transform: translateY(-#{$space-1}) scale(0.98);
  }

  &__search {
    display: flex;
    align-items: center;
    gap: aloha-size('sm', 'gap');
    padding: aloha-size('md', 'px');
    border-bottom: 1px solid $color-border;

    &:focus-within .base-combobox__search-icon {
      color: $color-accent;
    }
  }

  // Icon/text sizes and placeholder style match the tenders/opportunities
  // views-panel search (16px slate-400 icon, 9px gap, 13px text; the global
  // ::placeholder rule is italic — search fields override it to normal).
  &__search-icon {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin-right: 5px;
    color: $color-slate-400;
  }

  &__search-input {
    flex: 1 1 auto;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: $color-text-body;
    font-family: inherit;
    font-size: 13px;

    &::placeholder {
      color: $color-text-muted;
      font-style: normal;
    }
  }

  &__selectall {
    display: flex;
    align-items: center;
    width: 100%;
    padding: aloha-size('sm', 'py') aloha-size('md', 'px');
    border: none;
    border-bottom: 1px solid $color-border;
    background: transparent;
    color: $color-text-link;
    font-family: inherit;
    font-weight: $font-weight-bold;
    text-transform: uppercase;
    letter-spacing: $letter-spacing-wide;
    cursor: pointer;

    &.is-active {
      background-color: $color-accent-100;
    }

    &:hover,
    &.is-highlighted {
      background-color: $color-hover;
    }
  }

  &__options {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: aloha-size('xs', 'py') 0;
  }

  &__empty {
    margin: 0;
    padding: aloha-size('sm', 'py') aloha-size('md', 'px');
    color: $color-text-muted;
  }

  &__menu-clear {
    flex: 0 0 auto;
    display: block;
    margin: $space-1 $space-2 $space-2;
    border-top: 1px solid $color-border;
    padding-top: $space-1;
  }

  // Option rows (__row, __expand, indentation) are styled in BaseComboBoxNode,
  // which renders them — scoped styles here would not reach its inner elements.

  // Per-size control metrics, driven entirely by the shared $aloha-sizes map.
  @each $size, $tokens in $aloha-sizes {
    &--#{$size} .base-combobox__control {
      min-height: map-get($tokens, 'height');
      padding: map-get($tokens, 'py') map-get($tokens, 'px');
      font-size: map-get($tokens, 'font-size');
    }

    &--#{$size} .base-combobox__caret,
    &--#{$size} .base-combobox__clear {
      width: map-get($tokens, 'icon-size');
      height: map-get($tokens, 'icon-size');
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .base-combobox__control,
    .base-combobox__caret,
    .base-combobox__chip-remove,
    .base-combobox__clear {
      transition-duration: 0ms;
    }

    // Keep the dropdown fade for comprehension, drop the movement.
    .cbx-menu-enter,
    .cbx-menu-leave-to {
      transform: none;
    }
  }
}
</style>
